import { InvoiceStatus } from "@wingspanhq/payments/dist/interfaces";
import { WSQueryCache } from "@ws-react-query";
import {
  BulkActionOnProgress,
  BulkActionProgress,
  bulkActionFactory,
  useForegroundBulkAction
} from "../../../modules/BulkActions";
import { QUERY_PAYABLE, QUERY_PAYABLES } from "../../../query/payments/keys";
import { paymentsService } from "../../../services/payments";
import { PayablesTableItemData } from "./PayablesTable";

function processDeletePayables(
  data: PayablesTableItemData[],
  onProgress: BulkActionOnProgress,
  onSuccess?: () => void
): Promise<BulkActionProgress> {
  const bulkAction = bulkActionFactory<PayablesTableItemData>({
    onProgress,
    action: async item => {
      if (item.payable?.status !== InvoiceStatus.Draft) {
        throw new Error(
          `Cannot delete payable with status "${item.payable?.status}". Only draft payables can be deleted.`
        );
      }

      return await paymentsService.payable.delete(
        item.payable?.payableId || ""
      );
    },
    onFinish: ({ successItems }) => {
      WSQueryCache.invalidateQueries(QUERY_PAYABLES);
      successItems.forEach(item => {
        WSQueryCache.invalidateQueries([
          QUERY_PAYABLE,
          item.payable?.payableId
        ]);
      });
      onSuccess?.();
    }
  });

  return bulkAction.run(data).then(({ successItems, failedItems }) => ({
    total: data.length,
    processed: successItems.length + failedItems.length,
    successful: successItems.length,
    failed: failedItems.length
  }));
}

export function useBulkDeletePayables() {
  const runBulkAction = useForegroundBulkAction();

  return (items: PayablesTableItemData[], onSuccess?: () => void) => {
    const count = items.length;
    const pluralized = count > 1 ? "payables" : "payable";

    return runBulkAction({
      actionName: "Delete payables",
      itemCount: count,
      confirmDescription: `Are you sure you want to permanently delete ${count.toLocaleString()} draft ${pluralized}? This action cannot be undone.`,
      confirmLabel: "Delete",
      confirmDestructive: true,
      prepare: async () => items,
      process: async (data, onProgress) =>
        processDeletePayables(data, onProgress, onSuccess)
    });
  };
}
